import React, { useEffect, useMemo, useState } from 'react'
import Transition from 'components/atoms/Transition'
import StyledActionsDialog from 'components/atoms/StyledActionsDialog'
import StyledModalDialog from 'components/atoms/StyledModalDialog'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { Button, Loader } from 'sputnik-ui'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  DialogContent,
  DialogTitle,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core'
import { fetchAccessCodeHistory } from 'store/codes/operations'
import AccessCodeHistory from 'components/molecules/AccessCodeHistory'
import { normalizeAccessCode } from 'utils/helpers'

export default function AddressAccessCodesHistory({
  open,
  handleClose,
  ...props
}) {
  dayjs.extend(isBetween)
  const { t } = useTranslation(['keys', 'common'])
  const dispatch = useDispatch()
  const intercoms = useSelector(({ devicesNew }) => devicesNew.intercoms)

  const intercomUuids = useMemo(
    () => intercoms?.map((inter) => inter?.uuid),
    [intercoms]
  )

  const dateFormat = 'YYYY-MM-DD'
  const dateRange = useMemo(
    () => [
      dayjs().subtract(2, 'day').format(dateFormat),
      dayjs().subtract(1, 'day').format(dateFormat),
      dayjs().format(dateFormat),
    ],
    [dateFormat]
  )
  const [choosenKeyInfo, setChoosenKeyInfo] = useState({})

  useEffect(() => {
    if (open) {
      // Запрашиваем весь период, который показываем в таблице:
      // от начала позавчерашних суток до конца текущих.
      const start = dayjs().subtract(2, 'day').startOf('day').toISOString()
      const end = dayjs().endOf('day').toISOString()
      dispatch(
        fetchAccessCodeHistory({
          intercomUuids,
          startDate: start,
          endDate: end,
        })
      )
    }
  }, [dispatch, intercomUuids, open])
  const codesHistory = useSelector(({ codes }) => codes.history || {})
  const allCodes = useSelector(({ codes }) => codes.list || [])

  const countFor = (timestamps, date) => {
    if (!Array.isArray(timestamps)) {
      return 0
    }
    let counter = 0
    timestamps.forEach((timestamp) => {
      if (
        dayjs(timestamp).isBetween(
          dayjs(date).startOf('day'),
          dayjs(date).endOf('day')
        )
      ) {
        counter += 1
      }
    })
    return counter
  }

  // Коды сопоставляем по нормализованному виду, а показываем так, как они
  // записаны: в списке подъезда — приоритетно, иначе как пришли из истории.
  const codeRows = useMemo(() => {
    const rows = {}

    const row = (code) => {
      const key = normalizeAccessCode(code)

      if (!rows[key]) {
        rows[key] = { label: code, counts: {} }
        dateRange.forEach((date) => {
          rows[key].counts[date] = 0
        })
      }

      return rows[key]
    }

    // Сначала заводим нули по всем кодам подъезда, затем добавляем историю:
    // один код может открывать несколько панелей, счётчики по ним суммируются.
    allCodes.forEach((code_obj) => row(code_obj.value))

    Object.values(codesHistory).forEach((info) => {
      Object.entries(info).forEach(([code, timestamps]) => {
        const { counts } = row(code)
        dateRange.forEach((date) => {
          counts[date] += countFor(timestamps, date)
        })
      })
    })

    return Object.values(rows)
  }, [codesHistory, allCodes, dateRange])

  return (
    <StyledModalDialog
      open={open}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition}
      onClose={handleClose}
      PaperProps={{
        style: { borderRadius: 20, maxHeight: '100vh' },
      }}
      {...props}
    >
      <DialogTitle>{t('keys:access_code.history.title')}</DialogTitle>
      <DialogContent>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('keys:units.access_code_plural')}</TableCell>
              {dateRange.map((date) => (
                <TableCell key={date}>{date}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          {codesHistory ? (
            <TableBody>
              {codeRows.map(({ label: code, counts }) => (
                <TableRow tabIndex={-1} key={code}>
                  <TableCell component="th" scope="row" padding="dense">
                    {code}
                  </TableCell>
                  {dateRange.map((date) => (
                    <Tooltip
                      key={date}
                      placement="top"
                      title={
                        counts[date] > 0
                          ? t('keys:actions.see_specific_history')
                          : t('keys:access_code.history.no_history')
                      }
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        padding="dense"
                        onClick={() =>
                          counts[date] > 0 && setChoosenKeyInfo({ code, date })
                        }
                        style={{
                          cursor: counts[date] > 0 ? 'pointer' : 'default',
                          textAlign: 'center',
                        }}
                      >
                        {counts[date] > 0 ? (
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: '#eee',
                              fontSize: 13,
                              fontWeight: 500,
                            }}
                          >
                            {counts[date]}
                          </div>
                        ) : (
                          counts[date]
                        )}
                      </TableCell>
                    </Tooltip>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <Loader msg={t('messages:content.loading')} fluid />
          )}
        </Table>
      </DialogContent>
      <StyledActionsDialog>
        <Button onClick={handleClose} tertiary>
          {t('devices:video_archive.back')}
        </Button>
      </StyledActionsDialog>
      <AccessCodeHistory
        open={!!choosenKeyInfo.code}
        handleClose={() => setChoosenKeyInfo({})}
        codeNum={choosenKeyInfo.code}
        dateFormat={dateFormat}
        date={dayjs(choosenKeyInfo.date)}
        codesHistory={codesHistory}
        intercomUuids={intercomUuids}
      />
    </StyledModalDialog>
  )
}
